/**
 * Push 권한 요청 UX 오케스트레이션.
 *
 * fcmPush.ts 의 low-level helper + pushPromptStore 위에 구축한 통합 진입점.
 * 모든 트리거(첫 진입/로그인/회원가입/설정 토글/큐 참여)가 ensurePushPermission 으로 모임.
 *
 * 정책:
 *  - granted/unsupported → noop
 *  - canAsk → OS 다이얼로그를 1회 표시. 새로 허용되면 registerPushDevice() 재실행해
 *             디바이스를 messageable 상태로 만든다.
 *  - denied → allowSettingsPrompt=true 일 때만 in-app "설정으로 이동" 확인을 띄운다.
 *             (첫 진입/로그인/회원가입 등 passive 트리거는 false 로 호출해 nag 하지 않음.)
 */
import { getNativePushPermission, requestNativePushPermission, registerPushDevice } from "./fcmPush";
import { usePushPromptStore } from "../store/pushPromptStore";
import { isNative, nativeWarn } from "./index";

export type PushPermissionState = "granted" | "canAsk" | "denied" | "unsupported";

export async function getPushPermissionState(): Promise<PushPermissionState> {
  if (!isNative()) return "unsupported";
  const { granted, canRequest } = await getNativePushPermission();
  if (granted) return "granted";
  return canRequest ? "canAsk" : "denied";
}

/**
 * 통합 진입점. 어떤 트리거에서든 호출.
 * - granted/unsupported → noop
 * - canAsk → OS 다이얼로그 1회 표시. 새로 허용되면 registerPushDevice() 재실행.
 * - denied → allowSettingsPrompt=true 일 때만 in-app "설정으로 이동" 확인 (store 경유).
 */
export async function ensurePushPermission(opts?: { allowSettingsPrompt?: boolean }): Promise<PushPermissionState> {
  const allowSettingsPrompt = opts?.allowSettingsPrompt ?? false;
  const state = await getPushPermissionState();
  if (state === "granted") {
    // 권한은 있으나 토큰이 비어있을 수 있다(로그인 시점엔 권한 OFF 였거나, 등록이 토큰 미발급으로
    // 스킵됐던 경우). 설정 ON 등으로 도달하면 멱등 재등록으로 토큰을 채운다
    // (같은 installationId 면 BE 가 기존 행을 갱신한다).
    void registerPushDevice();
    return "granted";
  }
  if (state === "canAsk") {
    const granted = await requestNativePushPermission();
    if (granted) {
      void registerPushDevice();
      return "granted";
    }
    return "denied";
  }
  if (state === "denied" && allowSettingsPrompt) {
    usePushPromptStore.getState().openSettings();
  }
  return state;
}

/**
 * 설정 확인 다이얼로그의 primary 버튼에서 호출 — OS 알림 설정으로 라우팅.
 *
 * 한 번 거부된 뒤에는 OS 다이얼로그를 다시 띄울 수 없어 설정으로 보내는 것이 유일한 출구다.
 * (종전에는 OneSignal 의 `requestPermission(fallbackToSettings)` 가 이 일을 대신했다.)
 */
export async function openPushSettings(): Promise<void> {
  if (!isNative()) return;
  try {
    const { NativeSettings, AndroidSettings, IOSSettings } = await import("capacitor-native-settings");
    await NativeSettings.open({
      optionAndroid: AndroidSettings.AppNotification,
      optionIOS: IOSSettings.App,
    });
  } catch (err) {
    nativeWarn("push:openSettings", err);
  }
}
